// What a feed card says about someone else's workout.
//
// The card used to render a title, a type and a duration, and only when the
// post carried plan_data — so a post without it showed a name, a timestamp and
// nothing else. The question this answers is what a mate actually wants to
// know, which is more than "Michael trained":
//
//   • what it was            — the session's own title
//   • how long, how hard     — duration, RPE, heart rate, power
//   • what was in it         — the headline movements, which is also what
//                              makes the Copy button mean something
//   • what they said about it — the notes, the only genuinely human line in
//                              the whole record ("Trained with Joe")
//
// Pure, so all of it is testable without rendering anything.

import { showWeight } from "./units";
import { isCountedUnit } from "./planNodeUtils";

const RECOVERY_TYPES = new Set(["rest", "transition", "setrest"]);

const isRecovery = (node) =>
  RECOVERY_TYPES.has(String(node?.type || "").toLowerCase());

/** Every leaf that represents work, in plan order. */
export const workLeaves = (node, out = []) => {
  if (!node) return out;
  const children = Array.isArray(node.children) ? node.children : [];
  if (children.length === 0) {
    if (!isRecovery(node)) out.push(node);
    return out;
  }
  for (const child of children) workLeaves(child, out);
  return out;
};

/** Top-level blocks — the chapters of the session, not every nested group. */
export const countBlocks = (plan) =>
  (Array.isArray(plan?.children) ? plan.children : []).filter(
    (child) => Array.isArray(child?.children) && child.children.length > 0,
  ).length;

/**
 * Distinct movement names in plan order. Repeats collapse — a circuit run
 * three times is still "Squat, Press, Row", and listing it nine times tells
 * nobody anything.
 */
export const headlineMovements = (plan, limit = 3) => {
  const seen = new Set();
  const names = [];
  for (const leaf of workLeaves(plan)) {
    const label = String(leaf.label || leaf.exercise_name || "").trim();
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(label);
  }
  return { shown: names.slice(0, limit), total: names.length };
};

/** The heaviest working load in the session, which is the number people ask about. */
export const heaviestLoad = (plan) => {
  let best = 0;
  for (const leaf of workLeaves(plan)) {
    const candidates = [Number(leaf.weight)];
    const scheme = leaf.setScheme?.weight;
    if (Array.isArray(scheme)) candidates.push(...scheme.map(Number));
    for (const value of candidates) {
      if (Number.isFinite(value) && value > best) best = value;
    }
  }
  return best > 0 ? best : null;
};

/** Total prescribed reps, a fair proxy for volume on a strength session. */
export const totalReps = (plan) => {
  let reps = 0;
  for (const leaf of workLeaves(plan)) {
    if (!isCountedUnit(leaf.unit)) continue;
    const value = Number(leaf.value);
    if (Number.isFinite(value) && value > 0) reps += value;
  }
  return reps > 0 ? reps : null;
};

export const formatDurationLabel = (mins) => {
  const value = Number(mins);
  if (!Number.isFinite(value) || value <= 0) return null;
  if (value < 60) return `${Math.round(value)} min`;
  const hours = Math.floor(value / 60);
  const rest = Math.round(value % 60);
  return rest ? `${hours}h ${rest}min` : `${hours}h`;
};

/**
 * Trim notes to a readable excerpt, breaking on a word rather than mid-word.
 * The full text is never shown in the feed: a card is a headline, and a
 * paragraph of someone's training reflections isn't one.
 */
export const excerptNotes = (notes, limit = 160) => {
  const text = String(notes || "").replace(/\s+/g, " ").trim();
  if (!text) return null;
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
};

/**
 * Everything the card needs, in one shape. Every field is independently
 * optional: an imported ride has heart rate and no plan, a logged session has
 * a title and nothing else, and both should still read as a workout.
 */
export const summariseWorkoutPost = (post) => {
  const plan = post?.plan_data || null;
  const title =
    plan?.label || post?.workout_type || (plan ? "Workout" : null);

  const movements = plan ? headlineMovements(plan) : { shown: [], total: 0 };
  const hr = post?.telemetry_summary?.hr || null;
  const bike = post?.bike_summary || null;

  const metrics = [];
  const duration = formatDurationLabel(post?.duration_minutes);
  if (duration) metrics.push({ key: "duration", icon: "clock", label: duration });
  if (movements.total > 0) {
    metrics.push({
      key: "exercises",
      icon: "dumbbell",
      label: `${movements.total} exercise${movements.total === 1 ? "" : "s"}`,
    });
  }
  if (Number.isFinite(Number(hr?.avg)) && Number(hr.avg) > 0) {
    metrics.push({
      key: "hr",
      icon: "hr",
      label: Number.isFinite(Number(hr?.max))
        ? `${Math.round(hr.avg)} avg · ${Math.round(hr.max)} max bpm`
        : `${Math.round(hr.avg)} bpm avg`,
    });
  }
  if (Number.isFinite(Number(bike?.avg_power_watts)) && bike.avg_power_watts > 0) {
    metrics.push({
      key: "power",
      icon: "power",
      label: `${Math.round(bike.avg_power_watts)} W avg`,
    });
  }
  const load = plan ? heaviestLoad(plan) : null;
  if (load) {
    metrics.push({ key: "load", icon: "load", label: `${showWeight(load)} top` });
  }
  const rpe = Number(post?.rating);
  if (Number.isFinite(rpe) && rpe > 0) {
    metrics.push({ key: "rpe", icon: "effort", label: `RPE ${rpe}` });
  }

  return {
    title,
    metrics,
    movements: movements.shown,
    moreMovements: Math.max(0, movements.total - movements.shown.length),
    notes: excerptNotes(post?.notes),
    // Enough to be worth a card of its own rather than a bare byline.
    hasDetail: Boolean(title) || metrics.length > 0,
  };
};
